import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { actionGastosGet, apiGastosInstance, authHeaderGastos } from "../../redux/actions/gastos/gastos";

import { Button, Card, Input, InputNumber, DatePicker, Row, Col, Typography, Space, Modal, Form, Skeleton, ConfigProvider } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { SearchOutlined, DollarOutlined } from "@ant-design/icons";
import { showHerramientasEffect } from "../../components/utils/antWaveEffect";
import "./GastosPage.css";

dayjs.locale("es");

const { Title, Text } = Typography;
const { TextArea } = Input;

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export default function GastosPage() {
  const dispatch = useDispatch();
  const { items = [], loading } = useSelector((state) => state.gastos);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");

  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const lastFetchKey = useRef("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const key = searchDebounced;
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    dispatch(actionGastosGet({ search: searchDebounced || undefined }));
  }, [dispatch, searchDebounced]);

  const hoy = dayjs().format("YYYY-MM-DD");
  const inicioMes = dayjs().startOf("month").format("YYYY-MM-DD");

  const counts = useMemo(() => {
    const totalMes = items.filter((g) => g.fecha >= inicioMes).reduce((a, g) => a + parseFloat(g.monto || 0), 0);
    const totalHoy = items.filter((g) => g.fecha === hoy).reduce((a, g) => a + parseFloat(g.monto || 0), 0);
    return { totalMes, totalHoy };
  }, [items, hoy, inicioMes]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "hoy") return items.filter((g) => g.fecha === hoy);
    if (activeFilter === "mes") return items.filter((g) => g.fecha >= inicioMes);
    return items;
  }, [items, activeFilter, hoy, inicioMes]);

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      await apiGastosInstance.post("/gastos", {
        concepto: values.concepto.trim(),
        monto: values.monto,
        fecha: values.fecha.format("YYYY-MM-DD"),
        nota: values.nota?.trim() || null,
      }, { headers: authHeaderGastos() });
      toast.success("Gasto registrado exitosamente");
      setAddOpen(false);
      form.resetFields();
      dispatch(actionGastosGet({ search: searchDebounced || undefined }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar el gasto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfigProvider wave={{ showEffect: showHerramientasEffect }}>
      <main className="gas-main">
        <div className="gas-content">
          <div className="gas-filters-panel">
            <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
              <Title level={2} className="gas-title">Gastos</Title>
              <Text className="gas-subtitle">Registro de gastos de la tienda.</Text>
            </Space>

            <Row gutter={[16, 14]}>
              <Col xs={24}>
                <div className="gas-field-label">Buscador</div>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por concepto..."
                  suffix={<SearchOutlined className="gas-input-suffix" />}
                  className="gas-control"
                  allowClear
                />
              </Col>
            </Row>
          </div>

          <div className="gas-stats-row">
            <Card className={`gas-stat-card ${activeFilter === "hoy" ? "gas-stat-active" : ""}`} hoverable onClick={() => setActiveFilter((p) => (p === "hoy" ? "todos" : "hoy"))}>
              <Space align="center" size={10}>
                <div className="gas-stat-icon gas-stat-icon-total"><DollarOutlined /></div>
                <div>
                  <div className="gas-stat-value">{money(counts.totalHoy)}</div>
                  <div className="gas-stat-label">Gasto de hoy</div>
                </div>
              </Space>
            </Card>
            <Card className={`gas-stat-card ${activeFilter === "mes" ? "gas-stat-active" : ""}`} hoverable onClick={() => setActiveFilter((p) => (p === "mes" ? "todos" : "mes"))}>
              <Space align="center" size={10}>
                <div className="gas-stat-icon gas-stat-icon-doc"><DollarOutlined /></div>
                <div>
                  <div className="gas-stat-value">{money(counts.totalMes)}</div>
                  <div className="gas-stat-label">Gasto del mes</div>
                </div>
              </Space>
            </Card>
          </div>

          <div className="gas-toolbar">
            <div className="gas-toolbar-left">
              <h4>Gastos ({filteredItems.length})</h4>
            </div>
            <>
              <Button type="primary" icon={<i className="fa-solid fa-plus" />} onClick={() => setAddOpen(true)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                Registrar gasto
              </Button>
            </>
          </div>

          {!loading && filteredItems.length === 0 && (
            <div className="gas-empty">Sin gastos que coincidan con los filtros.</div>
          )}

          <div className="gas-list">
            {loading && Array.from({ length: 3 }).map((_, i) => (
              <div key={`sk-${i}`} className="gas-card">
                <div className="gas-card-accent" />
                <div className="gas-card-body">
                  <Skeleton active title={{ width: "40%" }} paragraph={{ rows: 1 }} />
                </div>
              </div>
            ))}

            {!loading && filteredItems.map((g) => (
              <div key={g.id_gasto} className="gas-card">
                <div className="gas-card-accent" />
                <div className="gas-card-body">
                  <div className="gas-card-main">
                    <div className="gas-card-desc">{g.concepto}</div>
                    <div className="gas-card-meta">
                      <span className="gas-card-meta-item"><i className="fa-solid fa-calendar" /> {g.fecha}</span>
                      {g.usuario_nombre && <span className="gas-card-meta-item gas-card-meta-user">{g.usuario_nombre}</span>}
                    </div>
                    {g.nota && <div className="gas-card-notes">{g.nota}</div>}
                  </div>
                  <div className="gas-card-right">
                    <div className="gas-card-monto">{money(g.monto)}</div>
                    <div className="gas-card-actions">
                      <button className="gas-btn-details" onClick={() => navigate(`/app/gastos/${g.id_gasto}`)}>VER</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Modal
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={handleSave}
        okText="Registrar gasto"
        cancelText="Cancelar"
        title="Nuevo gasto"
        confirmLoading={saving}
        centered
        okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}
      >
        <Form form={form} layout="vertical" initialValues={{ fecha: dayjs() }}>
          <Form.Item name="concepto" label="Concepto" rules={[{ required: true, message: "Requerido" }]}>
            <Input placeholder="Ej. Compra de bolsas para empaque" autoFocus />
          </Form.Item>
          <Form.Item name="monto" label="Monto" rules={[{ required: true, message: "Requerido" }]}>
            <InputNumber style={{ width: "100%" }} min={0.01} precision={2} prefix="$" placeholder="0.00" />
          </Form.Item>
          <Form.Item name="fecha" label="Fecha" rules={[{ required: true, message: "Requerido" }]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="nota" label="Nota (opcional)">
            <TextArea rows={3} maxLength={500} showCount placeholder="Detalles adicionales..." />
          </Form.Item>
        </Form>
      </Modal>
    </ConfigProvider>
  );
}
